/**
 * Link codes look like `7-lantern-fox`: a nameplate number the rendezvous
 * service hands out, then two or three words from a fixed speakable list.
 * The words are the SPAKE2 password; the number only routes the exchange.
 */

export const LINK_WORDS: readonly string[] = [
    "acorn",
    "amber",
    "anchor",
    "apple",
    "arrow",
    "atlas",
    "autumn",
    "badge",
    "bagel",
    "bamboo",
    "banjo",
    "basil",
    "beacon",
    "berry",
    "bison",
    "blaze",
    "bloom",
    "bonus",
    "breeze",
    "brick",
    "bridge",
    "bronze",
    "bubble",
    "butter",
    "cabin",
    "cactus",
    "camel",
    "candle",
    "canoe",
    "canyon",
    "carbon",
    "cargo",
    "castle",
    "cedar",
    "cello",
    "cherry",
    "chess",
    "cider",
    "cinema",
    "circus",
    "citrus",
    "clover",
    "cobalt",
    "cocoa",
    "comet",
    "copper",
    "coral",
    "cotton",
    "cousin",
    "crane",
    "crayon",
    "cricket",
    "crystal",
    "cumin",
    "curry",
    "cypress",
    "daisy",
    "dancer",
    "delta",
    "denim",
    "desert",
    "dial",
    "diesel",
    "dinghy",
    "dolphin",
    "domino",
    "dragon",
    "drift",
    "dune",
    "eagle",
    "ember",
    "engine",
    "falcon",
    "fennel",
    "ferry",
    "fiddle",
    "fjord",
    "flame",
    "flint",
    "forest",
    "fossil",
    "fox",
    "frost",
    "galaxy",
    "garden",
    "garlic",
    "gecko",
    "ginger",
    "glacier",
    "globe",
    "goblin",
    "granite",
    "grape",
    "gravel",
    "guitar",
    "hammer",
    "harbor",
    "harvest",
    "hazel",
    "helium",
    "heron",
    "honey",
    "horizon",
    "hotel",
    "igloo",
    "indigo",
    "iris",
    "island",
    "ivory",
    "jacket",
    "jaguar",
    "jasmine",
    "jelly",
    "jigsaw",
    "juniper",
    "kayak",
    "kettle",
    "kiwi",
    "koala",
    "lagoon",
    "lantern",
    "laser",
    "lava",
    "lemon",
    "lentil",
    "lilac",
    "lime",
    "lion",
    "lizard",
    "llama",
    "lobster",
    "locket",
    "lotus",
    "lunar",
    "magnet",
    "mango",
    "maple",
    "marble",
    "meadow",
    "melon",
    "meteor",
    "mint",
    "mirror",
    "mocha",
    "monsoon",
    "moose",
    "mosaic",
    "motor",
    "mural",
    "nectar",
    "nickel",
    "noodle",
    "nutmeg",
    "oasis",
    "ocean",
    "olive",
    "onion",
    "opal",
    "orbit",
    "orchid",
    "otter",
    "oyster",
    "paddle",
    "panda",
    "papaya",
    "parrot",
    "pasta",
    "pebble",
    "pelican",
    "pepper",
    "piano",
    "pickle",
    "pillow",
    "pilot",
    "pine",
    "planet",
    "plum",
    "pocket",
    "poppy",
    "puffin",
    "pumpkin",
    "puzzle",
    "quartz",
    "quill",
    "rabbit",
    "radar",
    "radish",
    "raven",
    "reef",
    "ribbon",
    "river",
    "robot",
    "rocket",
    "rubber",
    "saddle",
    "saffron",
    "salmon",
    "sandal",
    "satin",
    "scarf",
    "shadow",
    "sierra",
    "silver",
    "sketch",
    "socket",
    "sonar",
    "spark",
    "spider",
    "spruce",
    "squid",
    "static",
    "summit",
    "sunset",
    "tango",
    "teapot",
    "temple",
    "tiger",
    "timber",
    "tomato",
    "topaz",
    "torch",
    "trumpet",
    "tulip",
    "tundra",
    "turtle",
    "umbrella",
    "unicorn",
    "valley",
    "velvet",
    "violet",
    "violin",
    "volcano",
    "waffle",
    "walnut",
    "walrus",
    "wander",
    "wasabi",
    "willow",
    "window",
    "winter",
    "wizard",
    "yogurt",
    "zebra",
    "zephyr",
    "zinc",
    "zipper",
    "zodiac",
    "zucchini",
    "beetle",
    "biscuit",
    "blanket",
    "boulder",
    "bramble",
    "brook",
    "cabbage",
    "cameo",
]

export const LINK_PASSWORD_CONTEXT = "wildlark-link-v1"

export interface LinkCode {
    nameplate: number
    words: string[]
}

export function formatLinkCode(code: LinkCode): string {
    return [String(code.nameplate), ...code.words].join("-")
}

/** Password material for the key agreement: the words only, never the number. */
export function linkCodePassword(code: LinkCode): string {
    return code.words.join("-")
}

export function randomLinkWords(count: number): string[] {
    const picks = crypto.getRandomValues(new Uint8Array(count))
    return Array.from(picks, (value) => LINK_WORDS[value % LINK_WORDS.length] as string)
}

/**
 * Accepts a bare code, a code with spaces instead of dashes, or a pasted
 * chat message that contains one. Words must come from the list so a typo
 * is caught before the exchange burns the code.
 */
export function parseLinkCode(text: string): LinkCode | null {
    const normalized = text.toLowerCase().replace(/[‐-―]/g, "-")
    const match = /(?:^|[^0-9a-z])(\d{1,7})[-\s]+([a-z]+)[-\s]+([a-z]+)(?:[-\s]+([a-z]+))?(?![a-z])/.exec(normalized)
    if (!match) return null
    const nameplate = Number.parseInt(match[1] as string, 10)
    if (!Number.isSafeInteger(nameplate) || nameplate <= 0) return null
    const words = [match[2], match[3], match[4]].filter((word): word is string => typeof word === "string" && word.length > 0)
    if (!words.every((word) => LINK_WORDS.includes(word))) return null
    return { nameplate, words }
}

export function suggestLinkWords(prefix: string, limit = 6): string[] {
    const needle = prefix.trim().toLowerCase()
    if (!needle) return []
    return LINK_WORDS.filter((word) => word.startsWith(needle)).slice(0, limit)
}
